/**
 * Client-side canonical content for the "envoi général à tous les
 * copropriétaires" templates (standard v1..v4 + transaction estimation variants).
 *
 * STRICT RULE: the body MUST NEVER mention the detection of a DPE — this is
 * the explicit anti-disclosure rule from the functional spec. Content is
 * built entirely on the client (this module + the DOCX exporter); there is
 * no Twig mirror.
 */

export type DpeGeneralMailTemplateKey =
  | 'v1'
  | 'v2'
  | 'v3'
  | 'v4'
  | 'transaction-v1'
  | 'transaction-v2';

export interface DpeGeneralMailTemplateMeta {
  key: DpeGeneralMailTemplateKey;
  label: string;
  description: string;
  subject: string;
  paragraphs: string[];
  bulletGroups: { intro: string; items: string[] }[];
  closing: string[];
}

export const GENERAL_MAIL_STANDARD_OPTIONS: DpeGeneralMailTemplateKey[] = ['v1', 'v2', 'v3', 'v4'];

export const GENERAL_MAIL_TRANSACTION_OPTIONS: DpeGeneralMailTemplateKey[] = [
  'transaction-v1',
  'transaction-v2',
];

export const isGeneralMailTransactionTemplate = (
  templateKey: DpeGeneralMailTemplateKey,
): boolean => GENERAL_MAIL_TRANSACTION_OPTIONS.includes(templateKey);

export const isDpeGeneralMailTemplateKey = (
  value: string | null | undefined,
): value is DpeGeneralMailTemplateKey => (
  value === 'v1'
  || value === 'v2'
  || value === 'v3'
  || value === 'v4'
  || value === 'transaction-v1'
  || value === 'transaction-v2'
);

const COMMON_CLOSING = (referentFullName: string, syndicName: string, rdvUrl: string): string[] => ([
  'Si vous avez un projet à court ou moyen terme, ou simplement des questions, notre référent mutation-location se tient à votre disposition pour échanger avec vous lors d\'un rendez-vous téléphonique ou en visioconférence.',
  `Prendre rendez-vous : ${rdvUrl}`,
  'Cordialement,',
  `${referentFullName}\nRéférent Mutation Location${syndicName ? ` — ${syndicName}` : ''}`,
  'Conformément au RGPD, vous disposez d\'un droit d\'accès, de rectification, d\'effacement et de portabilité de vos données. Vous pouvez exercer ces droits en nous contactant.',
]);

export const DPE_GENERAL_MAIL_TEMPLATES: Record<DpeGeneralMailTemplateKey, Omit<DpeGeneralMailTemplateMeta, 'closing'>> = {
  v1: {
    key: 'v1',
    label: 'V1 — Version standard',
    description: 'Présentation pédagogique et professionnelle du référent mutation-location.',
    subject: 'Votre référent mutation-location au sein de votre copropriété',
    paragraphs: [
      'Cher Copropriétaire,',
      'Dans le cadre du suivi de votre copropriété, nous souhaitons vous rappeler l\'existence du service « Référent Mutation Location » mis à votre disposition par le syndic.',
      'Le rôle du référent mutation-location est d\'accompagner les copropriétaires qui ont un projet immobilier, qu\'il s\'agisse :',
    ],
    bulletGroups: [
      {
        intro: '',
        items: [
          'd\'une vente,',
          'd\'une mise en location,',
          'd\'une réflexion patrimoniale.',
        ],
      },
      {
        intro: 'À ce titre, nous pouvons notamment vous informer sur :',
        items: [
          'les documents obligatoires à prévoir en cas de vente, ainsi que les démarches administratives préparées par le syndic, notamment l\'état daté dans le cadre d\'une vente ;',
          'les éléments du règlement de copropriété utiles dans le cadre d\'une location.',
        ],
      },
      {
        intro: 'Nous pouvons également, si vous le souhaitez, vous transmettre différents rapports d\'informations concernant votre copropriété, notamment :',
        items: [
          'un rapport synthétique des diagnostics réalisés dans l\'immeuble depuis 2021 ;',
          'un rapport présentant les ventes intervenues récemment dans la copropriété et à proximité (rayon de 150 mètres).',
        ],
      },
    ],
  },
  v2: {
    key: 'v2',
    label: 'V2 — Version institutionnelle',
    description: 'Tonalité plus formelle, met l\'accent sur les obligations et démarches.',
    subject: 'Votre référent mutation-location au sein de votre copropriété',
    paragraphs: [
      'Cher Copropriétaire,',
      'Dans le cadre du suivi de votre copropriété, nous souhaitons vous rappeler l\'existence du service « Référent Mutation Location » mis à votre disposition par le syndic.',
      'Le rôle du référent mutation-location est d\'accompagner les copropriétaires qui ont un projet immobilier, qu\'il s\'agisse :',
    ],
    bulletGroups: [
      {
        intro: '',
        items: [
          'd\'une vente,',
          'd\'une mise en location,',
          'd\'une réflexion patrimoniale.',
        ],
      },
      {
        intro: 'À ce titre, nous pouvons notamment vous informer sur :',
        items: [
          'les documents obligatoires à prévoir en cas de vente ou de location ;',
          'les obligations liées aux diagnostics immobiliers ;',
          'les démarches préparées par le syndic, notamment l\'état daté ;',
          'ainsi que les éléments utiles du règlement de copropriété dans le cadre d\'une location.',
        ],
      },
      {
        intro: 'Nous pouvons également vous transmettre différents rapports d\'informations relatifs à votre copropriété :',
        items: [
          'synthèse des diagnostics réalisés dans l\'immeuble ;',
          'présentation des ventes intervenues récemment dans la copropriété et à proximité (rayon de 150 mètres) ;',
          'informations générales sur les évolutions réglementaires impactant les logements.',
        ],
      },
    ],
  },
  v3: {
    key: 'v3',
    label: 'V3 — Version patrimoniale',
    description: 'Approche valorisation/patrimoine, parle marché et positionnement du bien.',
    subject: 'Mieux comprendre le positionnement de votre bien dans votre copropriété',
    paragraphs: [
      'Cher Copropriétaire,',
      'Le marché immobilier évolue rapidement et de nombreux copropriétaires souhaitent aujourd\'hui mieux comprendre le positionnement de leur appartement, tant sur le plan énergétique que patrimonial.',
      'Dans ce contexte, le syndic met à votre disposition un référent mutation-location chargé d\'accompagner les copropriétaires ayant un projet de vente, de location ou de valorisation de leur bien.',
    ],
    bulletGroups: [
      {
        intro: 'À votre demande, nous pouvons notamment vous transmettre :',
        items: [
          'un rapport synthétique des diagnostics réalisés dans votre immeuble ;',
          'une présentation des ventes intervenues au sein de la copropriété et dans l\'environnement proche (rayon de 150 mètres).',
        ],
      },
      {
        intro: 'Ces informations permettent souvent :',
        items: [
          'de situer son appartement par rapport aux autres lots ;',
          'd\'anticiper certaines évolutions réglementaires ;',
          'd\'identifier les critères qui influencent la valeur d\'un bien ;',
          'ou encore de préparer un futur projet immobilier dans de meilleures conditions.',
        ],
      },
    ],
  },
  v4: {
    key: 'v4',
    label: 'V4 — Version réglementaire',
    description: 'Met en avant le contexte réglementaire et les évolutions énergétiques.',
    subject: 'Performance énergétique : informations utiles pour les copropriétaires',
    paragraphs: [
      'Cher Copropriétaire,',
      'Les évolutions réglementaires liées à la performance énergétique des logements prennent une place de plus en plus importante dans les projets de vente et de location.',
    ],
    bulletGroups: [
      {
        intro: 'Dans ce contexte, le syndic de votre copropriété met à votre disposition un référent mutation-location afin d\'aider les copropriétaires à mieux comprendre :',
        items: [
          'les obligations liées aux diagnostics énergétiques ;',
          'les impacts possibles des nouvelles réglementations ;',
          'les dispositifs concernant le diagnostic collectif et le Plan Pluriannuel de Travaux ;',
          'ainsi que les documents nécessaires dans le cadre d\'une vente ou d\'une mise en location.',
        ],
      },
      {
        intro: 'À votre demande, nous pouvons également vous transmettre plusieurs documents d\'information relatifs à votre copropriété :',
        items: [
          'synthèse des diagnostics réalisés dans l\'immeuble ;',
          'rapport sur les ventes récentes intervenues dans la copropriété et à proximité (rayon de 150 mètres).',
        ],
      },
    ],
  },
  'transaction-v1': {
    key: 'transaction-v1',
    label: 'V1',
    description: 'Estimation prix de vente — focus état daté et rapport d\'immeuble (sans notion de DPE).',
    subject: 'Vendre votre appartement : bien estimer le prix en tenant compte de l\'état daté de votre bien',
    paragraphs: [
      'Cher Copropriétaire,',
      'En tant que référent mutation-location au sein de votre syndic, je suis votre interlocuteur dédié pour toutes les questions liées à la vente ou à la location d\'un lot. À ce titre, je souhaite attirer votre attention sur un point souvent méconnu lors de la mise en vente d\'un appartement en copropriété.',
      'À l\'occasion de toute vente, le notaire demande au syndic un document appelé « état daté ». Ce document, remis à l\'acquéreur avant la signature de l\'acte définitif, récapitule notamment : les charges courantes et le budget de la copropriété, les sommes restant dues au syndicat, les travaux votés et à venir — dont ceux inscrits au plan pluriannuel de travaux (PPPT) — ainsi que les éventuelles procédures en cours.',
      'Si l\'acquéreur découvre à ce stade des travaux votés ou un niveau de charges qu\'il n\'avait pas anticipés, la vente peut s\'en trouver fragilisée : renégociation du prix convenu, retard, voire remise en cause de la transaction.',
      'C\'est pourquoi une estimation réalisée sans connaissance précise de la copropriété (PPPT, travaux votés, budget, procédures) peut se révéler éloignée du prix réellement réalisable.',
      'En tant que « Référent Mutation Location », mon rôle est de vous informer au plus près de votre copropriété en vous envoyant un rapport sur votre immeuble. Vous y trouverez notamment les premiers éléments concernant le marché immobilier local, les ventes réalisées dans un rayon de 150 mètres autour de l\'immeuble au cours des quatre dernières années (extrait du fichier impots.gouv) et d\'autres informations utiles.',
      'À la suite de cette réception, je pourrai, si vous le souhaitez, échanger avec vous pour vous commenter ce document et répondre à vos questions si vous avez un projet de vente.',
      'Si vous souhaitez ensuite aller plus loin — ou si vous disposez déjà d\'une estimation et souhaitez en obtenir une seconde, complémentaire, intégrant cette fois les informations propres à votre immeuble et à votre bien — je pourrai, après notre échange, si vous le souhaitez, convenir avec vous de la visite d\'une personne spécialisée du cabinet, qui se mettra en relation avec votre gestionnaire pour réaliser cette estimation en tenant compte de la vie de votre immeuble.',
      'Cette démarche est entièrement facultative et sans engagement : vos coordonnées ne seront communiquées à la personne en charge de l\'estimation que si vous en faites vous-même la demande.',
    ],
    bulletGroups: [],
  },
  'transaction-v2': {
    key: 'transaction-v2',
    label: 'V2',
    description: 'Estimation prix de vente — points clés (diagnostics, état daté, travaux, marché) sans notion de DPE.',
    subject: 'Vendre votre appartement : comment estimer le prix de votre bien avant une mise en vente ?',
    paragraphs: [
      'Cher Copropriétaire,',
      'En tant que référent mutation-location au sein de votre syndic, je suis votre interlocuteur dédié pour toutes les questions liées à la vente ou à la location d\'un lot. À ce titre, je souhaite attirer votre attention sur les points souvent méconnus lors de la mise en vente d\'un appartement en copropriété.',
      '1 / Les diagnostics : ils sont obligatoires pour la mise en vente. S\'ils sont purement informatifs, ils peuvent devenir des points bloquants pour votre acquéreur. Il est donc impératif de bien les interpréter pour estimer un bien.',
      '2 / L\'état daté : à l\'occasion de toute vente, le notaire demande au syndic un document appelé « état daté ». Ce document, remis à l\'acquéreur avant la signature de l\'acte définitif, récapitule notamment : les charges courantes et le budget de la copropriété, les sommes restant dues au syndicat, les travaux votés et à venir — dont ceux inscrits au plan pluriannuel de travaux (PPPT) — ainsi que les éventuelles procédures en cours.',
      'C\'est pourquoi une estimation réalisée sans connaissance précise de la copropriété (PPPT, travaux votés, budget, procédures) peut se révéler éloignée du prix réellement réalisable.',
      '3 / Les travaux de copropriété : si l\'acquéreur découvre à ce stade des travaux votés ou un niveau de charges qu\'il n\'avait pas anticipés, la vente peut s\'en trouver fragilisée : renégociation du prix convenu, retard, voire remise en cause de la transaction.',
      '4 / Les prix de vente dans votre immeuble et alentours : dans un marché en évolution, il est impératif de bien maîtriser les valeurs de ventes réalisées dans un rayon proche. Le prix de mise en vente doit se situer dans la valeur marché pour éviter une perte de l\'intérêt commercial de votre bien.',
      'Pour vous permettre d\'y voir plus clair, je peux, dans un premier temps et sur simple demande, vous adresser le rapport de copropriété de votre immeuble. Vous y trouverez déjà des éléments intéressants sur le marché immobilier : les ventes réalisées dans un rayon de 150 mètres autour de l\'immeuble au cours des quatre dernières années.',
      'À la suite de cette réception, je pourrai, si vous le souhaitez, échanger avec vous pour vous commenter ce document et répondre à vos questions si vous avez un projet de vente.',
      'Si vous disposez déjà d\'une estimation mais souhaitez en obtenir une seconde, complémentaire, intégrant cette fois les informations propres à votre immeuble — je pourrai, après notre échange, si vous le souhaitez, convenir avec vous de la visite d\'une personne spécialisée du cabinet, qui se mettra en relation avec votre gestionnaire pour réaliser cette estimation en tenant compte de la vie de votre immeuble.',
      'Cette démarche est entièrement facultative et sans engagement : vos coordonnées ne seront communiquées à la personne en charge de l\'estimation que si vous en faites vous-même la demande.',
    ],
    bulletGroups: [],
  },
};

export const buildDpeGeneralMailingText = (
  templateKey: DpeGeneralMailTemplateKey,
  referentFullName: string,
  syndicName: string,
  rdvUrl: string,
): { subject: string; body: string } => {
  const meta = DPE_GENERAL_MAIL_TEMPLATES[templateKey];
  const lines: string[] = [];

  meta.paragraphs.forEach((p) => {
    lines.push(p);
    lines.push('');
  });

  meta.bulletGroups.forEach((group) => {
    if (group.intro) {
      lines.push(group.intro);
    }
    group.items.forEach((item) => {
      lines.push(`- ${item}`);
    });
    lines.push('');
  });

  COMMON_CLOSING(referentFullName, syndicName, rdvUrl).forEach((line) => {
    lines.push(line);
    lines.push('');
  });

  return {
    subject: meta.subject,
    body: lines.join('\n').replaceAll(/\n{3,}/g, '\n\n').trim(),
  };
};
